<script setup lang="ts">
  import { computed, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { ChevronDown, Copy, LayoutGrid, Pencil, Plus, Trash2, User, Users } from "lucide-vue-next";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { FormDialog, FormField } from "@/components/episto-ui/form-dialog";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
  import { useGridsStore } from "@stores/survey/grids/grids";
  import type { Grid, GridType } from "@/types";

  const gridsStore = useGridsStore();
  const { currentGrid, defaultGrid, populationGrids, respondentGrids } = storeToRefs(gridsStore);
  const { selectGrid, create, rename, duplicate, remove } = gridsStore;

  const groups = computed(() => [
    { label: "Population grids", icon: Users, items: populationGrids.value },
    { label: "Respondent grids", icon: User, items: respondentGrids.value },
  ]);

  const gridTypes: { value: GridType; icon: typeof Users; label: string; description: string }[] = [
    {
      value: "population",
      icon: Users,
      label: "Population grid",
      description: "Compare populations: one synthesized AI answer per population, with citations.",
    },
    {
      value: "respondent",
      icon: User,
      label: "Respondent grid",
      description: "Read respondents side by side: survey answers, recodes and AI questions on each interview.",
    },
  ];

  type NameDialogMode = "create" | "rename" | "saveAsNew";

  const NAME_DIALOGS: Record<NameDialogMode, { title: string; confirmLabel: string }> = {
    create: { title: "New grid", confirmLabel: "Create" },
    rename: { title: "Rename grid", confirmLabel: "Rename" },
    saveAsNew: { title: "Save as new grid", confirmLabel: "Save as new" },
  };

  const selectorOpen = ref(false);
  const nameDialog = ref<NameDialogMode | null>(null);
  const deleteOpen = ref(false);
  const targetGrid = ref<Grid | null>(null);
  const draftName = ref("");
  const draftType = ref<GridType>("respondent");
  const saving = ref(false);

  const canSave = computed(() => !saving.value && !!draftName.value.trim());

  const openNameDialog = (mode: NameDialogMode, grid: Grid | null = null) => {
    targetGrid.value = grid;
    draftName.value = mode === "rename" && grid ? grid.name : "";
    draftType.value = "respondent";
    if (mode === "create") selectorOpen.value = false;
    nameDialog.value = mode;
  };

  const openDelete = (grid: Grid) => {
    targetGrid.value = grid;
    deleteOpen.value = true;
  };

  const pick = (token: string) => {
    selectGrid(token);
    selectorOpen.value = false;
  };

  const submitName = async () => {
    if (!canSave.value || !nameDialog.value) return;
    const mode = nameDialog.value;
    const name = draftName.value.trim();
    const token = targetGrid.value?.token;
    saving.value = true;
    try {
      if (mode === "create") await create(name, draftType.value);
      else if (mode === "rename" && token) await rename(token, name);
      else if (token) await duplicate(token, name);
      nameDialog.value = null;
      if (mode !== "rename") selectorOpen.value = false;
    } finally {
      saving.value = false;
    }
  };

  const submitDelete = async () => {
    if (targetGrid.value) await remove(targetGrid.value.token);
    selectorOpen.value = false;
  };
</script>

<template>
  <Popover v-model:open="selectorOpen">
    <PopoverTrigger as-child>
      <Button
        variant="outline"
        class="max-w-64 justify-start">
        <LayoutGrid class="size-4 shrink-0" />
        <span class="min-w-0 flex-1 truncate text-left">{{ currentGrid?.name }}</span>
        <ChevronDown class="size-4 shrink-0 text-muted-foreground" />
      </Button>
    </PopoverTrigger>

    <PopoverContent
      align="end"
      class="w-72 p-1">
      <div
        v-if="defaultGrid"
        class="group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-13 hover:bg-muted"
        :class="{ 'bg-muted': currentGrid?.default_grid }">
        <User class="size-3.5 shrink-0 text-muted-foreground" />
        <button
          type="button"
          class="min-w-0 flex-1 cursor-pointer truncate text-left"
          @click="pick(defaultGrid.token)">
          {{ defaultGrid.name }}
        </button>
        <Badge
          variant="neutral"
          class="shrink-0 text-2xs">
          Default
        </Badge>
        <button
          type="button"
          aria-label="Save as new grid"
          class="shrink-0 cursor-pointer text-muted-foreground opacity-0 hover:text-primary group-hover:opacity-100"
          @click.stop="openNameDialog('saveAsNew', defaultGrid)">
          <Copy class="size-3.5" />
        </button>
      </div>

      <template
        v-for="group in groups"
        :key="group.label">
        <template v-if="group.items.length">
          <div class="my-1 border-t" />
          <div class="heading-label px-2 pb-0.5 pt-1.5">{{ group.label }}</div>
          <div
            v-for="grid in group.items"
            :key="grid.token"
            class="group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-13 hover:bg-muted"
            :class="{ 'bg-muted': currentGrid?.token === grid.token }">
            <component
              :is="group.icon"
              class="size-3.5 shrink-0 text-muted-foreground" />
            <button
              type="button"
              class="min-w-0 flex-1 cursor-pointer truncate text-left"
              @click="pick(grid.token)">
              {{ grid.name }}
            </button>
            <button
              v-if="grid.renamable"
              type="button"
              aria-label="Rename grid"
              class="shrink-0 cursor-pointer text-muted-foreground opacity-0 hover:text-primary group-hover:opacity-100"
              @click.stop="openNameDialog('rename', grid)">
              <Pencil class="size-3.5" />
            </button>
            <button
              type="button"
              aria-label="Duplicate grid"
              class="shrink-0 cursor-pointer text-muted-foreground opacity-0 hover:text-primary group-hover:opacity-100"
              @click.stop="duplicate(grid.token)">
              <Copy class="size-3.5" />
            </button>
            <button
              v-if="grid.deletable"
              type="button"
              aria-label="Delete grid"
              class="shrink-0 cursor-pointer text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100"
              @click.stop="openDelete(grid)">
              <Trash2 class="size-3.5" />
            </button>
          </div>
        </template>
      </template>

      <div class="my-1 border-t" />
      <button
        type="button"
        class="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-13 hover:bg-muted"
        @click="openNameDialog('create')">
        <Plus class="size-4" />
        New grid
      </button>
    </PopoverContent>
  </Popover>

  <FormDialog
    :open="nameDialog !== null"
    :title="nameDialog ? NAME_DIALOGS[nameDialog].title : ''"
    :confirm-label="nameDialog ? NAME_DIALOGS[nameDialog].confirmLabel : ''"
    :max-width="nameDialog === 'create' ? '520px' : undefined"
    :loading="saving"
    :can-save="canSave"
    @update:open="(open) => !open && (nameDialog = null)"
    @save="submitName">
    <div
      v-if="nameDialog === 'create'"
      class="grid grid-cols-2 gap-3">
      <button
        v-for="type in gridTypes"
        :key="type.value"
        type="button"
        class="flex cursor-pointer flex-col items-start gap-1.5 rounded-lg border p-4 text-left transition-colors"
        :class="draftType === type.value ? 'border-primary bg-primary/5' : 'hover:bg-muted'"
        @click="draftType = type.value">
        <component
          :is="type.icon"
          class="size-5"
          :class="draftType === type.value ? 'text-primary' : 'text-muted-foreground'" />
        <span class="text-13 font-medium">{{ type.label }}</span>
        <span class="text-xs leading-snug text-muted-foreground">{{ type.description }}</span>
      </button>
    </div>
    <FormField label="Name">
      <Input
        v-model="draftName"
        placeholder="Grid name..."
        @keydown.enter="submitName" />
    </FormField>
  </FormDialog>

  <ConfirmDialog
    v-model:open="deleteOpen"
    title="Delete grid?"
    :description="`This will permanently delete &quot;${targetGrid?.name}&quot;. This action cannot be undone.`"
    confirm-label="Delete"
    auto-close
    @confirm="submitDelete" />
</template>
